import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Icon, palette } from '@m12s/component-library';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { useMutation } from '@apollo/client';

import { AppNav } from 'components/AppNav';
import MainLayout from 'components/Layouts/MainLayout';
import { getMachine } from 'lib/selectors/getMachine';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import Routes from 'lib/utils/routes';
import { H5 } from 'components/Text';
import { ModalButton } from 'components/LaborTicket/styled';
import { getWorkOrderOps } from 'lib/selectors/getWorkOrderOps';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import moment from 'moment';
import { formatDate, formatDuration, fromNow } from 'lib/utils/date';
import { getScopeNetPartsMade } from 'lib/selectors/getScopeNetPartsMade';
import { actionOpenLaborTicketModal } from 'lib/actions';
import {
  getRequestUpdateActivitySetOptions,
  stopJob,
} from 'components/LaborTicket/helpers';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { useTranslation } from 'react-i18next';
import { getLatestJobName } from 'lib/selectors/getLatestJobName';
import { UPDATE_ACTIVITY_SET } from 'lib/api/mutations';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import {
  Row,
  InfoContainer,
  InfoCard,
  InfoRow,
  WorkOrderHeading,
  OpButton,
  WorkOrderStat,
  WorkOrderSubStat,
  Footer,
  InfoGroup,
  StatGroup,
  StatItem,
  InfoGroupDivider,
} from './styled';

const JobInfoERP = ({
  handleOnClose = (history, machine) => {
    return history.push({ pathname: Routes.machineIdHomePath(machine.id) });
  },
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  /* eslint-disable no-param-reassign */
  const machine = useSelector(getMachine);
  const workOrderOps = useSelector(getWorkOrderOps);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const latestWorkOrder = latestActivitySet?.workOrderId;
  const latestActivity = useSelector(getLatestActivity);
  const parts = useSelector(getScopeNetPartsMade);
  const jobName = useSelector(getLatestJobName);
  const isPaused = useSelector(getIsPaused);
  const latestOp = workOrderOps.find((workOrder) => {
    return workOrder.workOrder.workOrderId === latestWorkOrder;
  });
  const unitDuration = latestActivitySet?.expectedUnitDuration;
  const partsRemaining = latestOp?.finishQuantity - parts;
  const timeToComplete =
    moment.duration(unitDuration).asMilliseconds() * partsRemaining;

  const estimatedCompletionDate = fromNow(timeToComplete);
  const scheduledCompletionDate = moment(
    latestOp?.workOrder?.scheduledFinishDate
  );

  const completionDelta = moment.duration(
    estimatedCompletionDate.diff(scheduledCompletionDate)
  );
  const isEarly = completionDelta.asSeconds() < 0;

  // Update Activity Set
  const requestUpdateActivitySetOptions = getRequestUpdateActivitySetOptions({
    dispatch,
    latestActivitySet,
    latestActivity,
    t,
    jobName,
  });

  const [requestUpdateActivitySet] = useMutation(
    UPDATE_ACTIVITY_SET,
    requestUpdateActivitySetOptions
  );

  return (
    <MainLayout.Container id="job-details-page" direction="column" overlay>
      <AppNav
        iconColor={palette.Grey700}
        color={palette.White}
        onClose={() => {
          handleOnClose(history, machine);
        }}
      >
        <H5
          style={{
            width: '100%',
            textAlign: 'left',
            paddingLeft: '1.5rem',
            fontWeight: '500',
          }}
          color={palette.Grey700}
        >
          Current work order operation
        </H5>
      </AppNav>
      {latestOp && (
        <>
          <Row>
            <InfoContainer>
              <InfoCard>
                <WorkOrderHeading>{t('Work Order Operation')}</WorkOrderHeading>
                <WorkOrderStat>
                  {latestOp?.workOrder?.workOrderId}
                </WorkOrderStat>
                <OpButton>
                  <Icon
                    icon={faEdit}
                    size="0.75rem"
                    style={{ marginRight: '0.375rem' }}
                  />
                  Change Operation
                </OpButton>
              </InfoCard>

              <InfoCard>
                <WorkOrderHeading>Parts Remaining</WorkOrderHeading>
                <WorkOrderStat>{partsRemaining}</WorkOrderStat>
                <WorkOrderSubStat>
                  {parts} / {latestOp?.finishQuantity}
                </WorkOrderSubStat>
              </InfoCard>
              <InfoCard>
                <WorkOrderHeading>Estimated Competion date</WorkOrderHeading>
                <WorkOrderStat>
                  {formatDate(fromNow(timeToComplete))}
                </WorkOrderStat>
                <WorkOrderSubStat
                  style={{
                    color: isEarly ? palette.Green600 : palette.Red600,
                    fontSize: '0.75rem',
                    fontWeight: '400',
                  }}
                >
                  {`↓ ${Math.round(completionDelta.asMinutes())} minutes ${
                    isEarly ? 'early' : 'late'
                  }`}
                </WorkOrderSubStat>
              </InfoCard>
            </InfoContainer>
          </Row>
          <Row height="100%">
            <InfoRow>
              <InfoGroup>
                <WorkOrderHeading style={{ marginBottom: '0.375rem' }}>
                  Part Operation
                </WorkOrderHeading>
                <WorkOrderSubStat style={{ marginBottom: '1rem' }}>
                  {latestOp?.workOrder?.partNumber}
                </WorkOrderSubStat>
                <StatGroup>
                  <StatItem>
                    <WorkOrderHeading style={{ marginBottom: '0.375rem' }}>
                      Lot
                    </WorkOrderHeading>
                    <WorkOrderSubStat style={{ marginBottom: '1rem' }}>
                      {latestOp?.workOrder?.lot}
                    </WorkOrderSubStat>
                  </StatItem>
                  <StatItem>
                    <WorkOrderHeading style={{ marginBottom: '0.375rem' }}>
                      Split
                    </WorkOrderHeading>
                    <WorkOrderSubStat style={{ marginBottom: '1rem' }}>
                      {latestOp?.workOrder?.split || 0}
                    </WorkOrderSubStat>
                  </StatItem>
                  <StatItem>
                    <WorkOrderHeading style={{ marginBottom: '0.375rem' }}>
                      Sub
                    </WorkOrderHeading>
                    <WorkOrderSubStat style={{ marginBottom: '1rem' }}>
                      {latestOp?.workOrder?.sub || 0}
                    </WorkOrderSubStat>
                  </StatItem>
                </StatGroup>
              </InfoGroup>
              <InfoGroupDivider />
              <InfoGroup>
                <WorkOrderHeading style={{ marginBottom: '0.375rem' }}>
                  WO Due Date
                </WorkOrderHeading>
                <WorkOrderSubStat style={{ marginBottom: '1rem' }}>
                  {formatDate(latestOp?.workOrder?.scheduledFinishDate)}
                </WorkOrderSubStat>
                <WorkOrderHeading>Estimated Time Remaining</WorkOrderHeading>
                <WorkOrderSubStat style={{ marginBottom: '1rem' }}>
                  {formatDuration(timeToComplete / 1000)}
                </WorkOrderSubStat>
              </InfoGroup>
              <InfoGroupDivider />
              <InfoGroup>
                <WorkOrderHeading style={{ marginBottom: '0.375rem' }}>
                  Scheduled Start Date
                </WorkOrderHeading>
                <WorkOrderSubStat style={{ marginBottom: '1rem' }}>
                  {formatDate(latestOp?.workOrder?.scheduledStartDate)}
                </WorkOrderSubStat>
                <WorkOrderHeading style={{ marginBottom: '0.375rem' }}>
                  Actual Start Date
                </WorkOrderHeading>
                <WorkOrderSubStat style={{ marginBottom: '1rem' }}>
                  {formatDate(latestActivitySet?.createdAt)}
                </WorkOrderSubStat>
              </InfoGroup>
            </InfoRow>
          </Row>
        </>
      )}
      <Footer>
        <ModalButton
          customStyles={{
            backgroundColor: palette.Indigo600,
          }}
          hoverStyles={{
            backgroundColor: palette.Indigo700,
          }}
          onClick={() => {
            handleOnClose(history, machine);
            if (isPaused) {
              return stopJob({
                requestUpdateActivitySet,
                latestActivitySet,
                latestActivity,
              });
            }
            return dispatch(
              actionOpenLaborTicketModal(LABOR_TICKET_ACTIONS.STOP_RUN)
            );
          }}
        >
          Stop Operation
        </ModalButton>
      </Footer>
    </MainLayout.Container>
  );
};

export default JobInfoERP;
